/**
 * CashStamp Storage Layer
 *
 * CashStamps are merchant-funded promotional BCH giveaways.
 * Each stamp has a BCH budget; customers claim a fixed reward per claim.
 * Claims are tracked by Nostr pubkey (one claim per account per stamp).
 *
 * All data is persisted in localStorage.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CashStamp {
  id: string;
  /** Merchant display name (snapshot at creation time) */
  merchantName: string;
  /** Promotion title, e.g. "Grand Opening Special" */
  title: string;
  /** Short description of the promotion */
  description: string;
  /** BCH reward per claim */
  rewardBCH: number;
  /** Total BCH budget allocated */
  totalBudgetBCH: number;
  /** Maximum claims = floor(totalBudget / reward) */
  maxClaims: number;
  /** BCH address where claimants should send claim requests */
  fundingAddress: string;
  /** Unix timestamp (ms) when the stamp was created */
  createdAt: number;
  /** Unix timestamp (ms) when the stamp expires */
  expiresAt: number;
  /** Optional merchant website URL */
  websiteUrl?: string;
  /** Optional BCH Local listing URL */
  bchLocalUrl?: string;
  /** Whether the merchant has disabled this stamp early */
  disabled: boolean;
  /** Claim records */
  claims: CashStampClaim[];
  /** View count (incremented on claim page load) */
  views: number;
}

export interface CashStampClaim {
  /** Nostr pubkey (hex) of the claimant */
  pubkey: string;
  /** BCH address the claimant provided */
  claimAddress: string;
  /** Unix timestamp (ms) when claimed */
  claimedAt: number;
  /** Status of the claim */
  status: 'pending' | 'paid' | 'failed';
  /** BCH txid once paid (optional) */
  txid?: string;
}

// ─── Storage Keys ────────────────────────────────────────────────────────────

const STAMPS_KEY = 'bch-pos:cashstamps';

// ─── CRUD ────────────────────────────────────────────────────────────────────

export function loadCashStamps(): CashStamp[] {
  try {
    const raw = localStorage.getItem(STAMPS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as CashStamp[];
  } catch {
    return [];
  }
}

export function saveCashStamps(stamps: CashStamp[]): void {
  try {
    localStorage.setItem(STAMPS_KEY, JSON.stringify(stamps));
  } catch (e) {
    console.warn('Failed to save cashstamps', e);
  }
}

export function getCashStamp(id: string): CashStamp | undefined {
  return loadCashStamps().find((s) => s.id === id);
}

export function upsertCashStamp(stamp: CashStamp): void {
  const stamps = loadCashStamps();
  const idx = stamps.findIndex((s) => s.id === stamp.id);
  if (idx >= 0) stamps[idx] = stamp;
  else stamps.unshift(stamp);
  saveCashStamps(stamps);
}

export function deleteCashStamp(id: string): void {
  saveCashStamps(loadCashStamps().filter((s) => s.id !== id));
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Check if a stamp is still active (not expired, not disabled, has budget) */
export function isStampActive(stamp: CashStamp): boolean {
  if (stamp.disabled) return false;
  if (Date.now() > stamp.expiresAt) return false;
  if (stamp.claims.length >= stamp.maxClaims) return false;
  return true;
}

/** Check if a pubkey has already claimed a stamp */
export function hasAlreadyClaimed(stamp: CashStamp, pubkey: string): boolean {
  return stamp.claims.some((c) => c.pubkey === pubkey);
}

/** Get the remaining claims available */
export function remainingClaims(stamp: CashStamp): number {
  return Math.max(0, stamp.maxClaims - stamp.claims.length);
}

/** Get the remaining BCH budget */
export function remainingBudget(stamp: CashStamp): number {
  const claimed = stamp.claims.length * stamp.rewardBCH;
  return Math.max(0, stamp.totalBudgetBCH - claimed);
}

/** Get total BCH distributed */
export function totalDistributed(stamp: CashStamp): number {
  return stamp.claims.filter((c) => c.status === 'paid' || c.status === 'pending').length * stamp.rewardBCH;
}

/** Record a claim on a stamp */
export function addClaim(stampId: string, claim: CashStampClaim): CashStamp | null {
  const stamps = loadCashStamps();
  const stamp = stamps.find((s) => s.id === stampId);
  if (!stamp) return null;
  // Duplicate check
  if (stamp.claims.some((c) => c.pubkey === claim.pubkey)) return stamp;
  stamp.claims.push(claim);
  saveCashStamps(stamps);
  return stamp;
}

/** Increment view count */
export function incrementViews(stampId: string): void {
  const stamps = loadCashStamps();
  const stamp = stamps.find((s) => s.id === stampId);
  if (!stamp) return;
  stamp.views = (stamp.views || 0) + 1;
  saveCashStamps(stamps);
}

/** Get aggregate stats across all stamps */
export function getCashStampStats(stamps: CashStamp[]): {
  totalClaims: number;
  totalDistributedBCH: number;
  totalViews: number;
  activeCount: number;
  expiredCount: number;
} {
  let totalClaims = 0;
  let totalDistributedBCH = 0;
  let totalViews = 0;
  let activeCount = 0;
  let expiredCount = 0;

  for (const stamp of stamps) {
    totalClaims += stamp.claims.length;
    totalDistributedBCH += totalDistributed(stamp);
    totalViews += stamp.views || 0;
    if (isStampActive(stamp)) activeCount++;
    else expiredCount++;
  }

  return { totalClaims, totalDistributedBCH, totalViews, activeCount, expiredCount };
}
